// LinkedIn paced sequence, derived (not stored).
//
// Instagram sequences are real documents: the run writes follow → comment → DM
// with explicit dueAt timestamps, and the dashboard reads them back. LinkedIn
// has no such document, deliberately — the hourly drip
// (mcp/src/repos/linkedinDrip.ts) is a state machine over the lead itself, and
// materializing a second copy of the plan would only drift from it.
//
// So this module PROJECTS the same three-step sequence out of the lead record
// the screen has already loaded: invite → follow-up 1 → follow-up 2, with each
// step's state and the one thing the drip will actually attempt today.
//
// DUPLICATED CONTRACT: the constants and state names below mirror
// mcp/src/repos/linkedinDrip.ts. Same rule as the rest of outreach — there is
// no shared package, so keep the two in sync by hand. The drip is
// authoritative; this file only reads what it wrote.
//   data.li_state     "to_invite" | "awaiting_accept" | "await_fu2" | "done" | "human"
//                     "demo_await_1" | "demo_await_2" — the post-demo ladder
//                     (2026-09-14). The demo link went out and the drip owns
//                     two bumps after it. Drip-owned like the templated
//                     states, so they park on automated:false the same way.
//                     "reply_due" — they wrote back. Out of the templated rail,
//                     but still automation's work (automated stays true): the
//                     reply gets drafted and approved in chat, not handed to a
//                     human queue.
//                     plus parked legacy states "quarantined" | "deferred"
//   data.automated    false parks the lead whatever li_state says. The drip's
//                     isHumanOwned reads this flag FIRST, so a hand-back that
//                     never touched li_state still stops every send.
//   data.li_username  publicIdentifier (required before anything can send)
//   data.li_note / li_followup_1 / li_followup_2   approved copy
//   data.li_demo_fu1 / li_demo_fu2   the post-demo ladder's two bumps.
//   data.li_demo_copy_at   when the demo rail's copy was authored. The demo
//                     link and both bumps are gated on THIS stamp, never on
//                     li_copy_at: a lead mid-conversation about their own app
//                     is not carrying the cold rails' copy.
//   data.li_copy_at   when the copy was authored. Same-UTC-day gate for the
//                     cold open and follow-up 2; on the acceptance path
//                     approved follow-up 1 is exempt up to a 30-day ceiling
//                     (linkedinDrip.ts acceptedFu1Sendable, 2026-09-07).
//   data.li_followup_1_sent_at   stamped by the drip when follow-up 1 is
//                     delivered. Present means the exemption is off for good:
//                     follow-up 1 is never sent twice on stale copy.
//   data.li_invited_at / li_accepted_at
//   data.sn_thread_id the lead's Sales Navigator thread id ("2-..."), stamped
//                     when an SN read finds their InMail thread. InMail lives
//                     in a mailbox /messaging/ cannot see, and the drip holds
//                     every send until that mailbox has been read too.
//   nextActionAt      YYYY-MM-DD, the scheduler

import { OutreachLead } from "../../../../types/OutreachMetrics";

// linkedinDrip.ts: FOLLOWUP_2_DELAY_DAYS, PENDING_LOST_DAYS.
export const FOLLOWUP_2_DELAY_DAYS = 4;
export const PENDING_LOST_DAYS = 21;
// linkedinDrip.ts: LINKEDIN_DRIP_WINDOW_UTC, default 13-23 (7:00-17:00 Managua).
export const SEND_WINDOW_UTC: [number, number] = [13, 23];
// linkedinDrip.ts: MAX_INVITES_PER_TICK / MAX_MESSAGES_PER_TICK, per hourly tick.
export const MAX_INVITES_PER_TICK = 2;
export const MAX_MESSAGES_PER_TICK = 3;

// linkedinDrip.ts DRIP_OWNED_STATES. Any other li_state value is somebody
// else's park, and so is data.automated === false on top of one of these.
const DRIP_OWNED_STATES = new Set([
  "to_invite",
  "awaiting_accept",
  "await_fu2",
  // The post-demo ladder (2026-09-14). Missing here, the two states fell to
  // "unknown" and this card told the operator the drip skips a lead it is in
  // fact bumping on a 3-day clock.
  "demo_await_1",
  "demo_await_2",
]);

// The ladder's delays (linkedinDrip.ts DEMO_FU1_DELAY_DAYS /
// DEMO_FU2_DELAY_DAYS) are deliberately NOT mirrored here. This file never
// computes a ladder date: it prints lead.nextActionAt, which the drip already
// wrote from its own constants, so a copy would be a second source of truth
// with no reader.

export type LinkedInStepType = "invite" | "followup_1" | "followup_2";

export type LinkedInStepStatus =
  // Already happened.
  | "sent"
  // The drip will attempt it today, inside the send window.
  | "due"
  // Its turn, but something the drip needs is missing — it parks instead.
  | "blocked"
  // Waiting on time or on the prospect.
  | "waiting"
  // Never happening: the sequence ended before this step.
  | "skipped";

export type LinkedInStep = {
  type: LinkedInStepType;
  label: string;
  status: LinkedInStepStatus;
  // The day it fired, the day it is expected, or what it is waiting on.
  when: string;
  // Why it is blocked, when it is.
  detail?: string;
};

export type LinkedInSequenceState =
  | "to_invite"
  | "awaiting_accept"
  | "await_fu2"
  // The post-demo ladder: bump 1 due, then bump 2 due.
  | "demo_await_1"
  | "demo_await_2"
  | "done"
  | "reply_due"
  | "human"
  | "quarantined"
  | "deferred"
  // li_state is still a drip state, but data.automated is false: a person took
  // the lead back and the drip will not touch it.
  | "handed_back"
  | "unknown";

export type LinkedInSequence = {
  leadId: string;
  name: string;
  username?: string;
  state: LinkedInSequenceState;
  stage: string;
  steps: LinkedInStep[];
  // One line: what the drip does with this lead today, or why it does nothing.
  today: string;
  // True when the drip will actually send something today.
  actsToday: boolean;
  // The step it would send today, for the day summary.
  todayStep?: LinkedInStepType;
  // Set when the drip cannot move this lead on its own. Nobody reads this
  // dashboard to find that out: the daily outreach run drafts the message and
  // asks for approval in chat. This string is the reference copy of why.
  needsHuman?: string;
  nextActionAt?: string;
  invitedAt?: string;
  acceptedAt?: string;
  // Days a connection request has been pending, against the 21-day give-up clock.
  pendingDays?: number;
};

const ymd = (d: Date): string => d.toISOString().slice(0, 10);

const STEP_LABEL: Record<LinkedInStepType, string> = {
  invite: "Connection request",
  followup_1: "Follow-up 1",
  followup_2: "Follow-up 2",
};

const daysBetween = (iso: string | undefined, now: Date): number | undefined => {
  if (!iso) return undefined;
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return undefined;
  return Math.floor((now.getTime() - parsed) / 86_400_000);
};

const addDays = (iso: string | undefined, days: number): string | undefined => {
  if (!iso) return undefined;
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return undefined;
  return ymd(new Date(parsed + days * 86_400_000));
};

const dayOf = (iso: string | undefined, fallback: string): string => {
  if (!iso) return fallback;
  const parsed = Date.parse(iso);
  return Number.isFinite(parsed) ? ymd(new Date(parsed)) : fallback;
};

// The drip only ships copy authored the same UTC day (linkedinDrip.ts
// copyIsFresh). Stale or unstamped copy parks the lead for the daily session.
//
// The negative-age test mirrors copyIsFresh's own `ageMs >= 0` (2026-09-07).
// A li_copy_at stamped one minute into the FUTURE is not fresh to the drip,
// and it is the branch the acceptance exemption below hands to the
// "dated in the future" reason, so the two readings have to agree here first.
export const copyIsFreshToday = (stampedAt: unknown, now: Date): boolean => {
  if (typeof stampedAt !== "string" || !stampedAt) return false;
  if (stampedAt.slice(0, 10) !== ymd(now)) return false;
  const ageMs = now.getTime() - Date.parse(stampedAt);
  return Number.isFinite(ageMs) && ageMs >= 0;
};

// Unfilled-placeholder lint, ported VERBATIM from linkedinDrip.ts PLACEHOLDER,
// which is itself the worker's reject_placeholder_copy
// (linkedin-worker/app/main.py:130). No "i" flag: Python's re there carries no
// IGNORECASE, so `<FIRST NAME>` is not a placeholder to either of them. No "g"
// flag: a stateful lastIndex would make the answer depend on call order.
const PLACEHOLDER = /\[[^\]\n]{2,60}\]|\{\{[^}\n]{1,60}\}\}|<[a-z][a-z ]{2,30}>/;
const hasPlaceholder = (text: unknown): boolean =>
  typeof text === "string" && PLACEHOLDER.test(text);

// linkedinDrip.ts ACCEPTED_COPY_MAX_AGE_DAYS. The drip reads
// LINKEDIN_DRIP_ACCEPTED_COPY_MAX_AGE_DAYS; a browser bundle cannot, so this
// is the default written out. If that env var is ever set on the worker, this
// number has to move with it.
export const ACCEPTED_COPY_MAX_AGE_DAYS = 30;

// Why follow-up 1 would NOT ship to a lead the moment they accept, or null
// when it ships (2026-09-07).
//
// This mirrors linkedinDrip.ts acceptedFu1Sendable, which exempts APPROVED
// follow-up 1 from the same-day copy rule on the acceptance path: the copy was
// authored and approved alongside the invite, and the gate it was held by was
// written for cold opens. Karen R. Koenig and four others parked for six days
// behind it while the dashboard told the operator to author copy that was
// already written. The exemption is bounded by the 30-day ceiling and refused
// outright once follow-up 1 has been delivered.
//
// Read-side only. The drip owns the decision; this file reports it, and the
// branch order here is acceptedFu1Sendable's branch order so the two answers
// cannot disagree. The strings are the operator-facing halves of the drip's
// own park note, and two of them name work that is NOT authoring: a
// future-dated stamp is a clock bug and an unreadable one is a data bug.
// Re-drafting fixes neither.
export const acceptedFollowup1Blocker = (
  data: Record<string, any>,
  now: Date
): string | null => {
  const copy = data.li_followup_1;
  if (typeof copy !== "string" || !copy.trim()) {
    return "no follow-up 1 copy written";
  }
  // Copy authored today ships on every path, exemption or not.
  if (copyIsFreshToday(data.li_copy_at, now)) return null;
  if (data.li_followup_1_sent_at) {
    return `follow-up 1 already went out on ${String(
      data.li_followup_1_sent_at
    ).slice(0, 10)}, and it is never sent twice; only same-day copy ships now`;
  }
  if (hasPlaceholder(copy)) {
    return "follow-up 1 carries an unfilled placeholder, which the worker refuses with a 422";
  }
  const stamped = data.li_copy_at;
  if (typeof stamped !== "string" || !stamped) {
    return "li_copy_at is unset, so the approved copy has no age to bound";
  }
  const ageMs = now.getTime() - Date.parse(stamped);
  if (!Number.isFinite(ageMs)) {
    return `li_copy_at "${stamped}" is not a readable timestamp, so the copy's age cannot be checked. Fix the stamp, not the copy.`;
  }
  if (ageMs < 0) {
    return `li_copy_at "${stamped}" is dated in the future, so no gate reads it as today's. That is a clock or stamping bug, not authoring work.`;
  }
  if (ageMs > ACCEPTED_COPY_MAX_AGE_DAYS * 86_400_000) {
    return `follow-up 1 copy is past the ${ACCEPTED_COPY_MAX_AGE_DAYS}-day ceiling for approved copy`;
  }
  return null;
};

const step = (
  type: LinkedInStepType,
  status: LinkedInStepStatus,
  when: string,
  detail?: string
): LinkedInStep => ({ type, label: STEP_LABEL[type], status, when, detail });

// Project one lead onto the three-step sequence. `now` is injectable so the
// "what happens today" line is testable.
export function deriveLinkedInSequence(
  lead: OutreachLead,
  now: Date = new Date()
): LinkedInSequence {
  const data = lead.data || {};
  const today = ymd(now);
  const username: string | undefined = data.li_username || lead.handle;
  const rawState = String(data.li_state || "to_invite");
  const storedState = (
    [
      "to_invite",
      "awaiting_accept",
      "await_fu2",
      "demo_await_1",
      "demo_await_2",
      "done",
      "reply_due",
      "human",
      "quarantined",
      "deferred",
    ].includes(rawState)
      ? rawState
      : "unknown"
  ) as LinkedInSequenceState;
  // Mirror linkedinDrip.ts isHumanOwned, which parks on TWO signals: an
  // li_state outside the drip's three, and data.automated === false. Only the
  // first was modelled here, so a lead handed back with automated:false but a
  // drip li_state read as "sends the connection request today" while the drip
  // skipped it untouched. The flag only overrides a state the drip would
  // otherwise own; "human", "done" and the legacy parks already read right.
  const handedBack =
    data.automated === false && DRIP_OWNED_STATES.has(storedState);
  const state: LinkedInSequenceState = handedBack ? "handed_back" : storedState;

  const due = Boolean(lead.nextActionAt && lead.nextActionAt <= today);
  const freshCopy = copyIsFreshToday(data.li_copy_at, now);
  const invitedAt: string | undefined = data.li_invited_at;
  const acceptedAt: string | undefined = data.li_accepted_at;
  const pendingDays = daysBetween(invitedAt, now);

  const base = {
    leadId: lead.id,
    name: lead.name,
    username,
    state,
    stage: lead.stage,
    nextActionAt: lead.nextActionAt,
    invitedAt,
    acceptedAt,
    pendingDays: storedState === "awaiting_accept" ? pendingDays : undefined,
  };

  // Handed back by hand: log_outreach_touch wrote automated:false and nobody
  // moved li_state. The step that was next in line is shown blocked rather
  // than due, because the drip filters this lead out of its own due query.
  if (state === "handed_back") {
    const parkedStep: LinkedInStepType =
      storedState === "to_invite"
        ? "invite"
        : storedState === "awaiting_accept"
        ? "followup_1"
        : "followup_2";
    const detail =
      `Flagged automated false, so the drip skips it. The daily run drafts the ` +
      `next message and asks for approval in chat.`;
    const parkOr = (
      type: LinkedInStepType,
      sentAt: string | undefined
    ): LinkedInStep =>
      type === parkedStep
        ? step(type, "blocked", "parked for a human", detail)
        : step(type, sentAt ? "sent" : "skipped", dayOf(sentAt, "-"));
    return {
      ...base,
      steps: [
        parkOr("invite", invitedAt),
        parkOr("followup_1", acceptedAt),
        parkOr("followup_2", undefined),
      ],
      today:
        `The drip sends nothing here. ${detail} To hand the lead back to the ` +
        `drip, log a touch with automated true and keep li_state on ` +
        `"${rawState}".`,
      actsToday: false,
      needsHuman: "Draft the next message in the daily run, then ask in chat.",
    };
  }

  // They wrote back. Out of the templated rail, but NOT handed to a person:
  // automation still owns the lead, and the reply gets drafted and approved in
  // chat. Shown as outstanding automation work, never as a human task.
  if (state === "reply_due") {
    return {
      ...base,
      steps: [
        step("invite", invitedAt ? "sent" : "skipped", dayOf(invitedAt, "-")),
        step("followup_1", acceptedAt ? "sent" : "skipped", dayOf(acceptedAt, "-")),
        step("followup_2", "skipped", "they replied"),
      ],
      today:
        lead.nextAction ||
        "They replied. The reply gets drafted for approval; no template sends.",
      actsToday: false,
    };
  }

  // A person owns the thread — the narrow case a machine cannot finish: a
  // suspected phantom send, or a send that could not be verified.
  // The drip will not touch this lead again.
  if (state === "human") {
    const needsHumanToday = !lead.nextActionAt || due;
    return {
      ...base,
      steps: [
        step("invite", invitedAt ? "sent" : "skipped", dayOf(invitedAt, "-")),
        step(
          "followup_1",
          acceptedAt ? "sent" : "skipped",
          dayOf(acceptedAt, "-")
        ),
        step("followup_2", "skipped", "handed to a human"),
      ],
      today: needsHumanToday
        ? lead.nextAction ||
          "The drip is out. The daily run reads the thread, drafts a reply with context, and asks for approval in chat."
        : `The next drafted reply is scheduled for ${lead.nextActionAt}.`,
      actsToday: false,
      needsHuman: needsHumanToday
        ? lead.nextAction || "Draft the reply in the daily run, then ask in chat."
        : undefined,
    };
  }

  if (state === "quarantined" || state === "deferred") {
    const quarantined = state === "quarantined";
    return {
      ...base,
      steps: [
        step("invite", invitedAt ? "sent" : "skipped", dayOf(invitedAt, "-")),
        step("followup_1", "skipped", quarantined ? "ICP quarantine" : "deferred"),
        step("followup_2", "skipped", "-"),
      ],
      today: quarantined
        ? "Quarantined pending ICP verification. The drip will not contact this lead."
        : `Deferred${lead.nextActionAt ? ` until ${lead.nextActionAt}` : ""}. Nothing sends automatically.`,
      actsToday: false,
    };
  }

  if (state === "done" || state === "unknown") {
    const ended = state === "done";
    return {
      ...base,
      steps: [
        step("invite", invitedAt ? "sent" : "skipped", dayOf(invitedAt, "-")),
        step(
          "followup_1",
          acceptedAt ? "sent" : "skipped",
          dayOf(acceptedAt, "-")
        ),
        step(
          "followup_2",
          ended && acceptedAt ? "sent" : "skipped",
          ended && acceptedAt ? "sequence closed" : "-"
        ),
      ],
      today: ended
        ? "Sequence finished. Nothing scheduled."
        : `Unknown drip state "${rawState}" — the drip skips this lead.`,
      actsToday: false,
      needsHuman:
        state === "unknown"
          ? `Unrecognized li_state "${rawState}". Raise it in chat.`
          : undefined,
    };
  }

  // Everything below is a live sequence, so a username to act on is the one
  // gate every state shares. The copy gate is NOT shared any more (2026-09-07):
  // linkedinDrip.ts keeps the strict same-day rule for the cold open and for
  // follow-up 2, and exempts approved follow-up 1 on the acceptance path, so
  // each state below applies its own.
  const blockers: string[] = [];
  if (!username) blockers.push("no LinkedIn username on the lead");

  // ── The post-demo ladder (2026-09-14) ─────────────────────────────────────
  // The demo link is out and the drip owns two bumps after it
  // (linkedinDrip.ts, the demo_await_1 / demo_await_2 branch). The three step
  // types above are the TEMPLATED rail's — invite, follow-up 1, follow-up 2 —
  // and the bumps are not any of them, so they are reported in `today` rather
  // than modelled as extra steps: a second sequence model here is exactly the
  // drift this file's header refuses.
  //
  // The copy gate mirrors the drip's demoLadderSendable, in its branch order:
  // missing copy, then an unfilled placeholder, then the same-day stamp. The
  // stamp read is li_demo_copy_at and never li_copy_at — the templated rails'
  // stamp says nothing about copy written for a lead who already has an app.
  if (state === "demo_await_1" || state === "demo_await_2") {
    const first = state === "demo_await_1";
    const bump = first ? 1 : 2;
    const field = first ? "li_demo_fu1" : "li_demo_fu2";
    const stampField = first ? "li_demo_fu1_sent_at" : "li_demo_fu2_sent_at";
    const sentStamp = data[stampField];
    const copy = data[field];
    // The drip's duplicate-send guard, mirrored (linkedinDrip.ts
    // demoLadderSentStamp). The stamp read is the one THIS rung writes, so
    // demo_await_2 carrying bump 1's stamp is the normal case, not a refusal.
    // It outranks the copy gate for the same reason it does there: fresh copy
    // on a spent rung must not read as a send the drip will make.
    if (typeof sentStamp === "string" && sentStamp) {
      blockers.push(
        `demo bump ${bump} already went out on ${dayOf(sentStamp, "an unreadable date")}; ` +
          `the drip refuses to send it twice and li_state needs moving by hand`
      );
    } else if (typeof copy !== "string" || !copy.trim()) {
      blockers.push(`no demo bump ${bump} copy written (data.${field})`);
    } else if (hasPlaceholder(copy)) {
      blockers.push(
        `demo bump ${bump} copy carries an unfilled placeholder, which the worker refuses with a 422`
      );
    } else if (!copyIsFreshToday(data.li_demo_copy_at, now)) {
      blockers.push("demo bump copy was not authored today (li_demo_copy_at)");
    }
    // Mode 5 outranks the ladder: once times have been proposed the 48-hour
    // slot bump is the follow-up of record and the drip yields the thread
    // untouched (linkedinDrip.ts onDemoLadder).
    const slotOffer = Boolean(data.slots_offered_at);
    const blocked = blockers.length > 0;
    const dueDate = lead.nextActionAt || "no date yet";
    // The drip writes its own words on every ladder touch ("Demo bump 1
    // (post-demo ladder)", "Park (demo ladder done)"). Prefer them the way the
    // reply_due branch above does: a park note the drip actually wrote beats a
    // line derived from li_state alone.
    const action = lead.nextAction || `Demo bump ${bump}`;
    return {
      ...base,
      steps: [
        step("invite", invitedAt ? "sent" : "skipped", dayOf(invitedAt, "-")),
        step(
          "followup_1",
          acceptedAt ? "sent" : "skipped",
          dayOf(acceptedAt, "-")
        ),
        step(
          "followup_2",
          "skipped",
          data.li_demo_sent_at
            ? `demo delivered ${dayOf(data.li_demo_sent_at, "-")}`
            : "demo delivered"
        ),
      ],
      today: slotOffer
        ? `${action} due ${dueDate}, but times have already been offered — the 48-hour slot bump owns this thread and the drip sends nothing.`
        : !due
        ? `${action} due ${dueDate}.`
        : blocked
        ? `${action} due ${dueDate} but parked: ${blockers.join(", ")}.`
        : `${action} due ${dueDate} — the drip sends it today.`,
      actsToday: due && !blocked && !slotOffer,
      needsHuman: due && blocked && !slotOffer ? blockers.join(", ") : undefined,
    };
  }

  if (state === "to_invite") {
    if (!freshCopy) blockers.push("copy was not authored today");
    if (!data.li_note) blockers.push("no connection note written");
    const blocked = blockers.length > 0;
    return {
      ...base,
      steps: [
        step(
          "invite",
          !due ? "waiting" : blocked ? "blocked" : "due",
          due ? "today" : lead.nextActionAt || "unscheduled",
          blocked ? blockers.join(", ") : undefined
        ),
        step("followup_1", "waiting", "when they accept"),
        step(
          "followup_2",
          "waiting",
          `${FOLLOWUP_2_DELAY_DAYS} days after they accept`
        ),
      ],
      today: !due
        ? `Nothing today — the connection request is scheduled for ${
            lead.nextActionAt || "no date yet"
          }.`
        : blocked
        ? `Connection request is due but parked: ${blockers.join(", ")}.`
        : "Sends the connection request today.",
      actsToday: due && !blocked,
      todayStep: due && !blocked ? "invite" : undefined,
      needsHuman: due && blocked ? blockers.join(", ") : undefined,
    };
  }

  if (state === "awaiting_accept") {
    // The drip re-checks the profile daily and sends follow-up 1 the moment it
    // sees a 1st-degree badge, so today's action is a check, not a scheduled send.
    const givingUp =
      typeof pendingDays === "number" && pendingDays >= PENDING_LOST_DAYS;
    // Approved follow-up 1 ships on acceptance without a same-day re-stamp, so
    // stale copy alone is not a blocker here. acceptedFollowup1Blocker mirrors
    // the drip's own acceptedFu1Sendable, including the "no copy written" case
    // the strict states report separately.
    const fu1Blocker = acceptedFollowup1Blocker(data, now);
    if (fu1Blocker) blockers.push(fu1Blocker);
    const blocked = blockers.length > 0;
    return {
      ...base,
      steps: [
        step("invite", "sent", dayOf(invitedAt, "sent")),
        step(
          "followup_1",
          givingUp ? "skipped" : !due ? "waiting" : blocked ? "blocked" : "due",
          givingUp
            ? "invite expired"
            : typeof pendingDays === "number"
            ? `pending ${pendingDays}d of ${PENDING_LOST_DAYS}`
            : "on acceptance",
          blocked && !givingUp ? blockers.join(", ") : undefined
        ),
        step(
          "followup_2",
          givingUp ? "skipped" : "waiting",
          givingUp
            ? "-"
            : `${FOLLOWUP_2_DELAY_DAYS} days after they accept`
        ),
      ],
      today: givingUp
        ? `Invite has been pending ${pendingDays} days — the drip marks this lost today. Withdraw the invite by hand.`
        : !due
        ? `Nothing today — next acceptance check ${
            lead.nextActionAt || "unscheduled"
          }.`
        : blocked
        ? `Checks acceptance today, but follow-up 1 would be parked: ${blockers.join(
            ", "
          )}.`
        : "Checks acceptance today, and sends follow-up 1 if they accepted.",
      actsToday: due && !blocked && !givingUp,
      todayStep: due && !blocked && !givingUp ? "followup_1" : undefined,
      needsHuman: givingUp
        ? "Withdrawing the invite needs LinkedIn by hand. Raise it in chat."
        : due && blocked
        ? blockers.join(", ")
        : undefined,
    };
  }

  // await_fu2
  const fu2Due = addDays(acceptedAt, FOLLOWUP_2_DELAY_DAYS);
  // Follow-up 2 keeps the strict same-day gate: it is the "no rush, last one"
  // bump, which is the copy class the drip's gate was written for.
  if (!freshCopy) blockers.push("copy was not authored today");
  if (!data.li_followup_2) blockers.push("no follow-up 2 copy written");
  const blocked = blockers.length > 0;
  return {
    ...base,
    steps: [
      step("invite", "sent", dayOf(invitedAt, "sent")),
      step("followup_1", "sent", dayOf(acceptedAt, "sent")),
      step(
        "followup_2",
        !due ? "waiting" : blocked ? "blocked" : "due",
        due ? "today" : lead.nextActionAt || fu2Due || "unscheduled",
        blocked ? blockers.join(", ") : undefined
      ),
    ],
    today: !due
      ? `Nothing today — follow-up 2 is scheduled for ${
          lead.nextActionAt || fu2Due || "no date yet"
        }.`
      : blocked
      ? `Follow-up 2 is due but parked: ${blockers.join(", ")}.`
      : "Sends follow-up 2 today.",
    actsToday: due && !blocked,
    todayStep: due && !blocked ? "followup_2" : undefined,
    needsHuman: due && blocked ? blockers.join(", ") : undefined,
  };
}

export type LinkedInDayPlan = {
  sequences: LinkedInSequence[];
  // What the drip intends to do today.
  invitesToday: number;
  messagesToday: number;
  // Due but parked — these need a human before they can move.
  blocked: number;
  // Threads a human owns.
  human: number;
  live: number;
  inWindow: boolean;
  windowLabel: string;
  // The caps are per hourly tick, so the window's hours bound the day.
  invitesCapPerTick: number;
  messagesCapPerTick: number;
};

const two = (n: number) => String(n).padStart(2, "0");

// Roll the per-lead projections into the day: what should happen, what is
// stuck, and whether the send window is open right now.
export function buildLinkedInDayPlan(
  leads: OutreachLead[],
  now: Date = new Date()
): LinkedInDayPlan {
  const sequences = leads
    .filter((l) => (l.channel || "").toLowerCase() === "li")
    .map((l) => deriveLinkedInSequence(l, now))
    // Today's work first, then what a human has to unblock, then everything
    // still live, then the closed ones.
    .sort((a, b) => {
      const rank = (s: LinkedInSequence) =>
        s.actsToday ? 0 : s.needsHuman ? 1 : s.state === "done" ? 3 : 2;
      const diff = rank(a) - rank(b);
      if (diff !== 0) return diff;
      return (a.nextActionAt || "9999").localeCompare(b.nextActionAt || "9999");
    });

  const hour = now.getUTCHours();
  const [start, end] = SEND_WINDOW_UTC;

  return {
    sequences,
    invitesToday: sequences.filter((s) => s.todayStep === "invite").length,
    messagesToday: sequences.filter(
      (s) =>
        s.todayStep === "followup_1" ||
        s.todayStep === "followup_2" ||
        // A post-demo bump is a DM on the same message budget, and it carries
        // no todayStep because it is not one of the three templated steps.
        (s.actsToday &&
          (s.state === "demo_await_1" || s.state === "demo_await_2"))
    ).length,
    blocked: sequences.filter((s) => Boolean(s.needsHuman)).length,
    human: sequences.filter(
      (s) => s.state === "human" || s.state === "handed_back"
    ).length,
    live: sequences.filter(
      (s) =>
        s.state !== "done" &&
        s.state !== "unknown" &&
        s.state !== "quarantined" &&
        s.state !== "deferred"
    ).length,
    inWindow: hour >= start && hour < end,
    windowLabel: `${two(start)}:00-${two(end)}:00 UTC`,
    invitesCapPerTick: MAX_INVITES_PER_TICK,
    messagesCapPerTick: MAX_MESSAGES_PER_TICK,
  };
}
